// OOB (out-of-band / OTP) channel editor used by the customer profile form
// (moved from the account form in mig 0017). Each entry picks an OOB type; the type's field_type decides what is
// captured (see oobFieldKind in @frt/shared-types). Since mig 0015 phone and
// device details live on registered assets: phone-kind types pick a phone
// number (its device follows the number's current assignment), every other
// type picks a device. Either picker can add a new asset in place.
// Validation mirrors the API (services/account/oob.ts).

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, selectClass } from '@/components/ui/field';
import { phoneNumbers } from '@/lib/api';
import { OOB_TEXT_MAX, isValidEmail, oobFieldKind } from '@frt/shared-types';
import type { OobEntry, OobInput, OobTypeRow } from '@frt/shared-types';
import { DevicePicker, PhonePicker } from '../assets/asset-forms';


export interface OobDraft {
  key: string;
  /** Existing channel (saved in place, keeping its status); null = new. */
  id: number | null;
  status: 'on' | 'off';
  oob_type_id: number; // 0 = not chosen
  contact_value: string;
  phone_number_id: number | null;
  device_id: number | null;
}

export type OobErrors = Partial<Record<keyof OobDraft, string>>;

let keySeq = 0;
export function newOobDraft(): OobDraft {
  return {
    key: `oob-${++keySeq}`,
    id: null,
    status: 'on',
    oob_type_id: 0,
    contact_value: '',
    phone_number_id: null,
    device_id: null,
  };
}

export function oobDraftFromEntry(e: OobEntry): OobDraft {
  return {
    key: `oob-${++keySeq}`,
    id: e.id,
    status: e.status,
    oob_type_id: e.oob_type_id,
    contact_value: e.contact_value ?? '',
    phone_number_id: e.phone_number_id,
    // For phone kinds device_id is the number's derived device, not an input.
    device_id: e.phone_number_id ? null : e.device_id,
  };
}

export function oobDraftToInput(d: OobDraft): OobInput {
  return {
    id: d.id,
    oob_type_id: d.oob_type_id,
    contact_value: d.contact_value.trim() || null,
    phone_number_id: d.phone_number_id,
    device_id: d.device_id,
  };
}

export function validateOobDraft(d: OobDraft, types: OobTypeRow[]): OobErrors {
  const errs: OobErrors = {};
  const type = types.find((t) => t.id === d.oob_type_id);
  if (!type) {
    errs.oob_type_id = 'Select an OOB type';
    return errs;
  }
  const v = d.contact_value.trim();
  switch (oobFieldKind(type.field_type)) {
    case 'phone':
      if (!d.phone_number_id) errs.phone_number_id = 'Select a phone number';
      return errs;
    case 'email':
      if (!isValidEmail(v)) errs.contact_value = 'Enter a valid email address';
      break;
    case 'text':
      if (!v) errs.contact_value = `${type.field_type} is required`;
      else if (v.length > OOB_TEXT_MAX) errs.contact_value = `Max ${OOB_TEXT_MAX} characters`;
      break;
  }
  if (!d.device_id) errs.device_id = 'Select a device';
  return errs;
}

export function OobEditor({
  entries,
  onChange,
  errors,
  types,
}: {
  entries: OobDraft[];
  onChange: (next: OobDraft[]) => void;
  errors: OobErrors[];
  types: OobTypeRow[];
}) {
  const update = (key: string, patch: Partial<OobDraft>) =>
    onChange(entries.map((e) => (e.key === key ? { ...e, ...patch } : e)));

  return (
    <div className="space-y-3">
      {entries.map((entry, i) => {
        const err = errors[i] ?? {};
        const type = types.find((t) => t.id === entry.oob_type_id);
        const kind = type ? oobFieldKind(type.field_type) : null;
        // Inactive types stay selectable only on the entry already using them.
        const typeOptions = types.filter((t) => t.active || t.id === entry.oob_type_id);
        return (
          <div key={entry.key} className="rounded border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                OOB entry {i + 1}
                {entry.status === 'off' && (
                  <span
                    className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                    title="Switch it On on the profile page"
                  >
                    Off
                  </span>
                )}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={entries.length <= 1}
                title={entries.length <= 1 ? 'At least one OOB entry is required' : undefined}
                onClick={() => onChange(entries.filter((e) => e.key !== entry.key))}
              >
                Remove
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="OOB Type *" error={err.oob_type_id}>
                <select
                  value={entry.oob_type_id}
                  onChange={(e) =>
                    update(entry.key, {
                      oob_type_id: Number(e.target.value),
                      contact_value: '',
                      phone_number_id: null,
                    })
                  }
                  className={selectClass}
                >
                  <option value={0}>— select —</option>
                  {typeOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.active ? '' : ' (inactive)'}
                    </option>
                  ))}
                </select>
              </Field>

              {kind === 'phone' && (
                <>
                  <Field label="Phone number *" error={err.phone_number_id}>
                    <PhonePicker
                      value={entry.phone_number_id}
                      onChange={(v) => update(entry.key, { phone_number_id: v })}
                    />
                  </Field>
                  <PhoneDeviceNote phoneId={entry.phone_number_id} />
                </>
              )}
              {kind === 'email' && (
                <Field label="Email address *" error={err.contact_value}>
                  <Input
                    type="email"
                    value={entry.contact_value}
                    onChange={(e) => update(entry.key, { contact_value: e.target.value })}
                  />
                </Field>
              )}
              {kind === 'text' && type && (
                <Field label={`${type.field_type} *`} error={err.contact_value}>
                  <Input
                    maxLength={OOB_TEXT_MAX}
                    value={entry.contact_value}
                    onChange={(e) => update(entry.key, { contact_value: e.target.value })}
                  />
                </Field>
              )}
              {kind !== null && kind !== 'phone' && (
                <Field label="Device *" error={err.device_id}>
                  <DevicePicker
                    value={entry.device_id}
                    onChange={(v) => update(entry.key, { device_id: v })}
                  />
                </Field>
              )}
            </div>
          </div>
        );
      })}
      <Button type="button" variant="secondary" onClick={() => onChange([...entries, newOobDraft()])}>
        + Add OOB entry
      </Button>
    </div>
  );
}

/** Read-only: the device (and owner) a chosen phone number is on right now. */
function PhoneDeviceNote({ phoneId }: { phoneId: number | null }) {
  const { data } = useQuery({
    queryKey: ['phone-numbers', { active: 'all' }],
    queryFn: () => phoneNumbers.list({ active: 'all' }),
    enabled: phoneId !== null,
  });
  const phone = data?.data.find((p) => p.id === phoneId);
  if (!phone) return null;
  return (
    <div className="text-sm">
      <span className="block text-slate-600">Device (from the number’s assignment)</span>
      <span className="block py-2">
        {phone.device_id ? phone.device_display : <span className="text-slate-500">No device</span>}
        {' · '}
        {/* New tab so the half-filled account form is kept. */}
        <Link
          to="/assets/phones/$id"
          params={{ id: String(phone.id) }}
          target="_blank"
          rel="noreferrer"
          className="text-brand-700 hover:underline"
        >
          reassign
        </Link>
      </span>
    </div>
  );
}
